import { Box } from "@mui/material";
import safeStringify from "fast-safe-stringify";

import { GetServerSideProps, NextPage } from "next";
import { useSearchParams } from "next/navigation";

import { Fragment, useEffect } from "react";

import { ContentfulNavigation } from "../../../../@types/contentfulNavigation";
import { IPageFields } from "../../../../@types/generated/contentful";
import { getHero } from "../../../../app/[slug]/component";
import ContentSectionWrapper from "../../../../components/content-section-wrapper";
import CountdownBar from "../../../../components/countdown-bar";
import PageLayout from "../../../../components/page/layout";
import { getContentfulRecipePage } from "../../../../utils/contentful/getContentfulRecipePage";
import generateTitle from "../../../../utils/page/generateTitle";
import getJointPageProperties, {
  JointPageProperties,
} from "../../../../utils/page/getJointPageProperties";
import { DOC_FLECK_TENANT_NAME } from "../../../../utils/tenant-names";

interface PageProps {
  isPreview?: boolean;
  page: IPageFields;
  previewData?: any;
  navigation: ContentfulNavigation;
  jointPageProperties: JointPageProperties;
}

/**
 * Top level page
 * @param {PageProps} props
 * @param {IPageFields} props.page
 * @returns JSX.Element
 */
const RecipePage: NextPage<PageProps> = ({
  isPreview,
  page,
  previewData,
  navigation,
  jointPageProperties,
}: PageProps) => {
  const {
    contentSections,
    heroTeaser,
    seoMetadata,
    countdownPage,
    noNavigation,
    tenant,
  } = page;
  const {
    fields: { title },
  } = seoMetadata;
  const { footerProps } = jointPageProperties;
  const {
    logoDark,
    logoLight,
    titleSuffix,
    bannerVisible,
    bannerText,
    bannerIcon,
  } = jointPageProperties.siteSettings;

  const searchParams = useSearchParams();

  useEffect(() => {
    if (isPreview && previewData?.internal !== "") {
      const previewElement = document.getElementById(previewData.internal);
      if (previewElement) {
        previewElement.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [isPreview, previewData.internal]);

  const fullTitle = generateTitle(title, titleSuffix);

  return (
    <>
      <PageLayout
        title={fullTitle}
        footer={footerProps}
        logoHeader={logoDark}
        logoFooter={logoLight}
        navigation={navigation}
        bannerVisible={bannerVisible}
        bannerText={bannerText}
        bannerIcon={bannerIcon}
        metadata={seoMetadata.fields}
        noNavigation={noNavigation}
        tenant={tenant}
      >
        {heroTeaser && getHero(heroTeaser, contentSections)}
        {contentSections?.map((section, i) => (
          <Fragment key={`section-${i}`}>
            <ContentSectionWrapper section={section} />
            {i == contentSections.length - 1 &&
            section.sys.contentType.sys.id == "recipeSection" ? (
              <Box sx={{ mt: 10 }}></Box>
            ) : (
              <></>
            )}
          </Fragment>
        ))}
        {countdownPage && (
          <CountdownBar {...countdownPage.fields}></CountdownBar>
        )}
      </PageLayout>
    </>
  );
};

export default RecipePage;

export const getServerSideProps: GetServerSideProps<PageProps> = async (
  context
) => {
  const slug = context.params?.slug?.toString() || "";

  let contentfulPage = await getContentfulRecipePage(
    slug,
    context.preview || false,
    context.previewData
  );

  if (contentfulPage === null) {
    return { notFound: true };
  }

  const jointPageProperties = await getJointPageProperties(
    context,
    DOC_FLECK_TENANT_NAME
  );

  const propsData = safeStringify({
    page: contentfulPage.fields,
    isPreview: context.preview || false,
    previewData: context.previewData || {
      env: process.env.NEXT_PUBLIC_CONTENTFUL_ENV,
      internal: "",
    },
    navigation: jointPageProperties.navigation,
    jointPageProperties,
    theme: DOC_FLECK_TENANT_NAME,
  });

  const props = JSON.parse(propsData);

  // pass page data to the page via props
  return {
    props,
  };
};
